import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  Check,
  CircleDollarSign,
  FileCheck2,
  Gavel,
  HelpCircle,
  LockKeyhole,
  Mail,
  MapPin,
  MessageSquareWarning,
  SearchCheck,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CTA_PATH = "/registro/abogado?next=/app/delegaciones/nueva&source=delegar";
const ATTRIBUTION_KEY = "colegalink_delegar_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function getAttribution() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_KEY) || "{}");
  } catch {
    return {};
  }
}

function track(event, properties = {}) {
  if (typeof window === "undefined") return;
  const payload = { event, page: "delegar", ...getAttribution(), ...properties };
  window.dataLayer?.push(payload);
  window.gtag?.("event", event, { page_location: window.location.href, ...payload });
}

function PrimaryCTA({ className = "", label = "Publicar una delegación", placement = "body" }) {
  const { user } = useAuth();
  const to = user ? "/app/delegaciones/nueva" : CTA_PATH;

  return (
    <Link
      to={to}
      onClick={() => track("delegar_cta_click", { placement, authenticated: Boolean(user) })}
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-teal-400 px-5 py-3 font-bold text-slate-950 shadow-lg shadow-teal-950/20 transition hover:-translate-y-0.5 hover:bg-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:ring-offset-2 focus:ring-offset-slate-950 ${className}`}
    >
      {label}
      <ArrowRight className="h-5 w-5" />
    </Link>
  );
}

const trustItems = [
  {
    icon: UserCheck,
    title: "Abogados verificados",
    text: "Reduce la incertidumbre de contactar perfiles desconocidos en redes o grupos.",
  },
  {
    icon: CircleDollarSign,
    title: "Condiciones claras",
    text: "Alcance, honorarios y condiciones quedan definidos antes de ejecutar la gestión.",
  },
  {
    icon: FileCheck2,
    title: "Gestión documentada",
    text: "La entrega y sus comprobantes quedan ordenados dentro de un mismo flujo.",
  },
];

const useCases = [
  "Audiencias y comparendos",
  "Presentación o retiro de escritos",
  "Revisión de expedientes",
  "Diligencias en tribunales y organismos",
  "Gestiones fuera de tu ciudad",
  "Apoyo territorial para tu estudio",
];

const steps = [
  {
    icon: CalendarClock,
    number: "01",
    title: "Publica lo que necesitas",
    text: "Indica tribunal, ciudad, fecha, alcance y antecedentes relevantes.",
  },
  {
    icon: SearchCheck,
    number: "02",
    title: "Revisa y acuerda",
    text: "Evalúa al profesional y deja claras las condiciones de la gestión.",
  },
  {
    icon: FileCheck2,
    number: "03",
    title: "Recibe la entrega",
    text: "Centraliza el estado, los respaldos y el cierre de la gestión.",
  },
];

const faqs = [
  {
    question: "¿Colega Link reemplaza el acuerdo profesional?",
    answer:
      "No. La plataforma ayuda a ordenar la delegación, sus condiciones y su entrega. Cada abogado sigue siendo responsable de revisar el encargo y aceptar sólo aquello que puede ejecutar competentemente.",
  },
  {
    question: "¿Cómo sé que quien acepta es abogado?",
    answer:
      "Colega Link incorpora un proceso de verificación profesional para reducir el riesgo de trabajar con identidades o perfiles informales.",
  },
  {
    question: "¿Qué ocurre con el precio y el pago?",
    answer:
      "El objetivo es que el honorario y sus condiciones queden definidos sin sorpresas. El flujo registra el estado de la gestión y del pago, sin presentarse como custodia financiera regulada.",
  },
  {
    question: "¿Sirve si necesito una gestión en otra región?",
    answer:
      "Sí. La plataforma está pensada para conectar abogados que requieren apoyo territorial con colegas disponibles en distintas ciudades de Chile.",
  },
];

export default function DelegarLanding() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const attribution = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) attribution[key] = value.slice(0, 160);
    });
    const source = params.get("source");
    if (source) attribution.source = source.slice(0, 80);

    if (Object.keys(attribution).length) {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    }

    const originalTitle = document.title;
    const title = "Delega sin improvisar | Colega Link";
    const description =
      "Abogados verificados, condiciones claras y gestión documentada para delegar audiencias y diligencias en Chile.";
    document.title = title;

    let descriptionTag = document.querySelector('meta[name="description"]');
    const originalDescription = descriptionTag?.getAttribute("content");
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute("content", description);

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    const originalCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://colegalink.cl/delegar");

    track("delegar_view");

    return () => {
      document.title = originalTitle;
      if (originalDescription) descriptionTag?.setAttribute("content", originalDescription);
      if (createdCanonical) canonical?.remove();
      else if (originalCanonical) canonical?.setAttribute("href", originalCanonical);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2" aria-label="Colega Link">
            <img src="/logo-isotipo.png" alt="" className="h-11 w-11 rounded-full object-cover shadow-md shadow-black/30" />
            <span className="text-lg font-bold tracking-tight">Colega Link</span>
            
          </Link>
          <PrimaryCTA label="Delegar ahora" placement="header" className="min-h-10 px-4 py-2 text-sm" />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(45,212,191,0.2),transparent_38%),radial-gradient(circle_at_80%_25%,rgba(59,130,246,0.13),transparent_32%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:px-8">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-300/30 bg-teal-300/10 px-4 py-2 text-sm font-semibold text-teal-200">
                <ShieldCheck className="h-4 w-4" />
                El fin de la delegación informal
              </div>
              <h1 className="max-w-4xl text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                Delega sin <span className="text-teal-300">improvisar.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                Deja atrás acuerdos dispersos por redes sociales y grupos de WhatsApp. Encuentra apoyo profesional para tu audiencia o gestión con identidad, condiciones y entrega ordenadas en un mismo flujo.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <PrimaryCTA label="Publicar mi gestión" placement="hero" />
                <button
                  type="button"
                  onClick={() => {
                    track("delegar_how_it_works", { placement: "hero" });
                    document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 font-semibold text-white transition hover:bg-white/10"
                >
                  Ver cómo funciona
                </button>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
                {["Abogados verificados", "Condiciones claras", "Gestión documentada"].map((item) => (
                  <span key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-teal-300" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-6 shadow-2xl shadow-black/30 sm:p-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-300">Una gestión bien delegada</p>
                  <h2 className="mt-2 text-2xl font-bold">Todo lo importante, visible</h2>
                </div>
                <LockKeyhole className="h-7 w-7 text-teal-300" />
              </div>
              <div className="space-y-4">
                {[
                  ["Profesional", "Identidad profesional comprobable"],
                  ["Encargo", "Alcance y antecedentes definidos"],
                  ["Honorarios", "Condiciones acordadas sin sorpresas"],
                  ["Entrega", "Estado y comprobantes documentados"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="mt-1 font-semibold text-slate-100">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-slate-900/60">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-300">Confianza para ambas partes</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">La certeza que un grupo de WhatsApp no puede darte.</h2>
              <p className="mt-4 text-lg leading-8 text-slate-300">
                El abogado mandante necesita saber quién hará la gestión y qué recibirá. El abogado delegado necesita saber qué debe hacer y bajo qué condiciones se le pagará.
              </p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {trustItems.map(({ icon: Icon, title, text }) => (
                <article key={title} className="rounded-2xl border border-white/10 bg-slate-950/60 p-6">
                  <Icon className="h-7 w-7 text-teal-300" />
                  <h3 className="mt-5 text-xl font-bold">{title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-300">Para el abogado mandante</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Mantén el control, aunque la gestión ocurra lejos.</h2>
              <div className="mt-8 space-y-4">
                {[
                  "Sabes quién realizará la gestión.",
                  "Dejas por escrito el alcance y las condiciones.",
                  "Recibes el respaldo acordado para cerrar el encargo.",
                ].map((text) => (
                  <div key={text} className="flex gap-3">
                    <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-teal-300" />
                    <p className="text-lg text-slate-300">{text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-blue-300">Para el abogado delegado</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Acepta encargos con expectativas claras.</h2>
              <div className="mt-8 space-y-4">
                {[
                  "Sabes exactamente qué debes hacer.",
                  "Conoces el honorario y sus condiciones.",
                  "Dejas constancia de lo entregado.",
                ].map((text) => (
                  <div key={text} className="flex gap-3">
                    <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-blue-300" />
                    <p className="text-lg text-slate-300">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/[0.03]">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-300">Qué puedes delegar</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Apoyo jurídico territorial cuando lo necesitas.</h2>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {useCases.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/50 p-5">
                  <Gavel className="h-5 w-5 shrink-0 text-teal-300" />
                  <span className="font-semibold text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="como-funciona" className="scroll-mt-24">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-300">Cómo funciona</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Tres pasos para reemplazar la improvisación por un proceso.</h2>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {steps.map(({ icon: Icon, number, title, text }) => (
                <article key={number} className="relative rounded-3xl border border-white/10 bg-slate-900/60 p-7">
                  <span className="absolute right-6 top-5 text-5xl font-black text-white/[0.05]">{number}</span>
                  <Icon className="h-8 w-8 text-teal-300" />
                  <h3 className="mt-6 text-xl font-bold">{title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-slate-900/60">
          <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="text-center">
              <HelpCircle className="mx-auto h-9 w-9 text-teal-300" />
              <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl">Preguntas antes de delegar</h2>
            </div>
            <div className="mt-10 space-y-4">
              {faqs.map(({ question, answer }) => (
                <details key={question} className="group rounded-2xl border border-white/10 bg-slate-950/60 p-5">
                  <summary className="cursor-pointer list-none pr-8 text-lg font-bold marker:hidden">{question}</summary>
                  <p className="mt-3 max-w-4xl leading-7 text-slate-400">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-teal-300/25 bg-gradient-to-br from-teal-300/20 via-slate-900 to-blue-500/10 p-8 text-center sm:p-12">
            <MapPin className="mx-auto h-9 w-9 text-teal-300" />
            <h2 className="mt-5 text-3xl font-black tracking-tight sm:text-5xl">Tu próxima gestión no tiene que depender de un contacto informal.</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
              Publica el encargo, acuerda las condiciones y mantén su avance documentado en Colega Link.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <PrimaryCTA label="Delegar con respaldo" placement="final" />
              <a
                href="mailto:contacto@colegalink.cl?subject=Necesito%20orientacion%20para%20delegar"
                onClick={() => track("delegar_contact_click", { placement: "final", channel: "email" })}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/15 px-5 py-3 font-semibold text-white transition hover:bg-white/10"
              >
                <Mail className="h-5 w-5" />
                Necesito orientación
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 rounded-2xl border border-amber-300/20 bg-amber-300/[0.06] p-5 text-sm text-slate-300">
            <MessageSquareWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
            <p>
              Colega Link facilita la conexión y el registro operativo del encargo. Cada profesional debe revisar sus obligaciones, plazos y competencias antes de aceptar una gestión.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span>© 2026 Colega Link · Conecta · Delega · Resuelve</span>
          <div className="flex gap-5">
            <Link to="/terminos" className="transition hover:text-slate-300">Términos</Link>
            <Link to="/" className="transition hover:text-slate-300">Volver al inicio</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
